// Hooks
import { useState } from 'react';

// Interfaces
import { InputHook } from '../interfaces/Input';

// Custom hook that handles a form input. It receives a function named validateValue that
// is also given a string and provides validation to that input returning true if it's 
// valid or a string with the error to return if it's not valid. It receives also a 
// defaultValue with the default string value and also a default error message that will 
// be show with higher priority than the one that returns the validateValue function.
// The useInput hook returns an InputHook object with:
//    - the current value of the input.
//    - a boolean indicating whether the value is valid.
//    - another boolean indicating if it has any error (an error happens when the value
//    is invalid and the user has untouched the input).
//    - a string with the errorMsg if there is one
//    - a function that handles the change of the input value. It should be provided on 
//    the onChange property of an input
//    - a function that handles the blur of the input value. It should be provided on 
//    the onBlur property of an input
//    - a reset function to reset the input state.

const useInput =
    (
        validateValue?: (value: string) => true | string,
        defaultValue?: string,
        defaultErrorMsg?: string,
    ): InputHook => {

        const [value, setValue] = useState<string>(defaultValue || '');
        const [inputTouched, setInputTouched] = useState<boolean>(false);

        const valueChangeHandler = (event?: React.FormEvent, value?: string) => {
            if(event) setValue((event.target as HTMLInputElement).value);
            if(value) setValue(value);
        };

        const inputBlurHandler = () => {
            setInputTouched(true);
        };

        const reset = () => {
            setValue('');
            setInputTouched(false);
        };

        const validation = validateValue ? validateValue(value) : true;
        const valueIsValid = validation === true || false;
        const hasError = !valueIsValid && inputTouched;
        const errorMsg = validation !== true && hasError ? (defaultErrorMsg || validation) : ''; 

        return {
            value,
            valueIsValid,
            hasError,
            errorMsg,
            valueChangeHandler,
            inputBlurHandler,
            reset
        };
    };

export default useInput;